import { CachedCurrentUserService, PlatformClientService } from '@pure/pure1-ui-platform-angular';
import { FeatureFlagDxpService } from '@pure1/data';
import { firstValueFrom, take } from 'rxjs';

import { configureFaHWLibrary } from './fa-hw-library-host.config';

import { EffectiveOrgService } from '../data/services/effective-org.service';
import { findOrgByUid } from '../data/utils/find-org-by-uid';
import { extractOrgUidFromPath } from '../data/utils/org-url';
import { pickEffectiveOrg } from '../data/utils/pick-effective-org';
import { OrgSwitcherService } from '../page/page-wrapper/org-switcher/org-switcher.service';
import { PureRedux } from '../redux/pure-redux.service';
import { AuthenticationService } from '../services/authentication.service';
import { AngularticsConfigService } from './angulartics-config.service';
import { AppcuesService } from './appcues.service';
import { DatadogLoggingConfigService } from './datadog-logging-config.service';
import { DatadogRumConfigService } from './datadog-rum-config.service';
import { ORG_URL_FF } from './guards/org-url-shared';
import { toEffectiveOrg } from './resolve-bootstrap-org-context';
import { UserChangedDetectorService } from './user-changed-detector.service';
import { CssUrlStyleManager } from '../services/css-url-style-manager.service';

export interface EagerServices {
    angularticsConfigService: AngularticsConfigService;
    datadogLoggingConfigService: DatadogLoggingConfigService;
    datadogRumConfigService: DatadogRumConfigService;
    cssUrlStyleManager: CssUrlStyleManager;
    appcuesService: AppcuesService;
    userChangedDetectorService: UserChangedDetectorService;
}

export function initializeApp(
    pureRedux: PureRedux,
    orgSwitcherService: OrgSwitcherService,
    effectiveOrgService: EffectiveOrgService,
    featureFlagService: FeatureFlagDxpService,
    platformClientService: PlatformClientService,
    currentUserService: CachedCurrentUserService,
    authenticationService: AuthenticationService,
    angularticsConfigService: AngularticsConfigService,
    datadogLoggingConfigService: DatadogLoggingConfigService,
    datadogRumConfigService: DatadogRumConfigService,
    cssUrlStyleManager: CssUrlStyleManager,
    appcuesService: AppcuesService,
    userChangedDetectorService: UserChangedDetectorService,
    doc: Document,
): () => Promise<unknown> {
    return async () => {
        // Register the fa-gui-hw hardware-visualization library host before any array-health
        // graph renders. Shared with the unit-test bootstrap (testing/global-test-setup.ts) so
        // both paths register identical config; the library throws if a class is constructed
        // before a host is registered.
        configureFaHWLibrary();

        const uiPlatformReady: Promise<void> = (doc.defaultView as any).dxp?.ready;
        if (!uiPlatformReady) {
            throw new Error('Failed to locate "window.dxp.ready", make sure UI Platform is loaded on the page');
        }
        await uiPlatformReady;
        // The effective org is normally resolved before bootstrap (main.ts → resolveBootstrapOrgContext),
        // so services constructed for this initializer already send the org headers. Resolve it here
        // only when that could not (e.g. an older UI Platform without window.dxp.getOrgs()).
        orgSwitcherService.loadOrgSwitcherItems();
        if (!effectiveOrgService.getEffectiveOrgSync()) {
            await primeEffectiveOrg(
                orgSwitcherService,
                effectiveOrgService,
                featureFlagService,
                platformClientService,
                currentUserService,
                doc,
            );
        }
        // Start introspect after the org is set so effectiveOrg$ is set when withLatestFrom fires.
        authenticationService.initialize();
        await initializeServices(pureRedux, {
            angularticsConfigService,
            datadogLoggingConfigService,
            datadogRumConfigService,
            cssUrlStyleManager,
            appcuesService,
            userChangedDetectorService,
        });
    };
}

export async function primeEffectiveOrg(
    orgSwitcherService: OrgSwitcherService,
    effectiveOrgService: EffectiveOrgService,
    featureFlagService: FeatureFlagDxpService,
    platformClientService: PlatformClientService,
    currentUserService: CachedCurrentUserService,
    doc: Document,
): Promise<void> {
    orgSwitcherService.loadOrgSwitcherItems();
    const [orgs, ffStatus] = await Promise.all([
        firstValueFrom(orgSwitcherService.userOrgs$),
        firstValueFrom(featureFlagService.getFeatureFlag(ORG_URL_FF)),
    ]);

    if (!ffStatus?.enabled) {
        const platformOrg = await firstValueFrom(platformClientService.getEffectiveOrg()).catch(() => null);
        if (platformOrg) {
            effectiveOrgService.setCurrentOrg({ ...platformOrg, opCompanyName: null });
        }
        return;
    }

    let homeOpCompanyUid: string | null = null;
    const urlMatch = findOrgByUid(orgs, extractOrgUidFromPath(doc.location.pathname));
    if (!urlMatch) {
        const user = await firstValueFrom(currentUserService.getCurrentUser()).catch(() => null);
        homeOpCompanyUid = user?.operatingCompany?.uid ?? null;
    }
    const seed = urlMatch ?? pickEffectiveOrg(orgs, null, homeOpCompanyUid);

    if (seed) {
        effectiveOrgService.setCurrentOrg(toEffectiveOrg(seed));
    }
}

export async function initializeServices(pureRedux: PureRedux, eager: EagerServices): Promise<void> {
    try {
        await firstValueFrom(pureRedux.init().pipe(take(1)));
    } catch (err) {
        console.warn('pureRedux.init() returned error', err);
    }
    eager.angularticsConfigService.run();
    eager.datadogLoggingConfigService.run();
    eager.datadogRumConfigService.run();
    eager.cssUrlStyleManager.run();
    eager.appcuesService.run(); // fire-and-forget
    eager.userChangedDetectorService.run();
}
